import { iframeDataBridge } from '@/iframe/iframeDataBridge';
import { authenticatedFetch, getApiUrl } from '@/utils/auth/authService';
import { log } from '@/utils/core/vueLogger.js';
import { CacheManager } from '@/utils/core/cacheManager.js';
import { registerQueueHandler, enqueueOperation } from '@/sync/offlineSyncQueue.js';
import { useTranslator } from '@/composables/useTranslator';

const userSettingCache = new CacheManager('VueCache', 'cache', {
	maxAge: 7 * 24 * 60 * 60 * 1000,
	memoryCacheTTL: 5 * 60 * 1000,
	logCategory: 'UserSetting',
	keyPrefix: () => `${iframeDataBridge.getCustomer() ?? 'unknown'}.${iframeDataBridge.getCurrentUser()?.id ?? 'unknown'}.userSettings.`,
});

function getUserSettingsCacheKey(data = null) {
	if (!data || typeof data !== 'object') {
		return 'user-settings:all';
	}

	const normalizedEntries = Object.entries(data)
		.filter(([, value]) => value !== undefined && value !== null && value !== '')
		.sort(([firstKey], [secondKey]) => firstKey.localeCompare(secondKey));

	if (!normalizedEntries.length) {
		return 'user-settings:all';
	}

	return `user-settings:${JSON.stringify(Object.fromEntries(normalizedEntries))}`;
}

export async function getUserSettings(data = null) {
	const cacheKey = getUserSettingsCacheKey(data);

	const memCached = userSettingCache.getFromMemory(cacheKey);
	if (memCached) {
		return memCached;
	}

	try {
		const cachedUserSettings = await userSettingCache.get(cacheKey);
		if (cachedUserSettings) {
			userSettingCache.setInMemory(cacheKey, cachedUserSettings);

			if (!iframeDataBridge.isOnline()) {
				log('info', 'UserSetting', '📴 Offline: Returning cached user settings');
				return cachedUserSettings;
			}

			setTimeout(() => refreshUserSettingsInBackground(data), 100);
			return cachedUserSettings;
		}

		if (!iframeDataBridge.isOnline()) {
			log('warn', 'UserSetting', '📴 Offline: No cached user settings available');
			return [];
		}

		return await fetchUserSettingsFromAPI(data);
	} catch (error) {
		log('error', 'UserSetting', `❌ Error getting user settings: ${error.message}`);

		try {
			const fallbackUserSettings = await userSettingCache.get(cacheKey);
			if (fallbackUserSettings) {
				log('info', 'UserSetting', '💾 Using fallback cached user settings');
				return fallbackUserSettings;
			}
		} catch (cacheError) {
			log('error', 'UserSetting', `❌ Cache fallback failed: ${cacheError.message}`);
		}

		return [];
	}
}

async function fetchUserSettingsFromAPI(data) {
	try {
		const apiUrl = getApiUrl(`Core/UserSettings`);
		const response = await authenticatedFetch(
			apiUrl,
			{
				method: 'GET',
				data: data,
			},
			'tokenSales'
		);

		if (!response.ok) {
			throw new Error(`HTTP error! status: ${response.status}`);
		}

		const userSettings = await response.json();

		const cacheKey = getUserSettingsCacheKey(data);
		await userSettingCache.put(cacheKey, userSettings);
		userSettingCache.setInMemory(cacheKey, userSettings);

		log('success', 'UserSetting', `✅ Fetched and cached ${userSettings.length} user settings`);
		return userSettings;
	} catch (error) {
		log('error', 'UserSetting', `❌ Failed to fetch user settings from API: ${error.message}`);
		throw error;
	}
}

async function refreshUserSettingsInBackground(data = null) {
	try {
		await fetchUserSettingsFromAPI(data);
		log('success', 'UserSetting', '🔄 Background refresh of user settings completed');
	} catch (error) {
		log('warn', 'UserSetting', `⚠️ Background refresh failed: ${error.message}`);
	}
}

registerQueueHandler('postUserSettings', async ({ userSettings, queryFilter }) => {
	const success = await postUserSettings(userSettings, queryFilter);
	if (!success) throw new Error('Failed to post user settings');
});

export async function postUserSettings(userSettings, queryFilter = null) {
	const t = useTranslator();
	const toast = iframeDataBridge.getIframeWindow()?.toast;

	if (!iframeDataBridge.isOnline()) {
		const cacheKey = getUserSettingsCacheKey(queryFilter);
		const plainUserSettings = JSON.parse(JSON.stringify(userSettings));
		await userSettingCache.put(cacheKey, plainUserSettings);
		userSettingCache.setInMemory(cacheKey, plainUserSettings);
		log('info', 'UserSetting', '💾 Offline: Optimistic cache update applied');

		await enqueueOperation('postUserSettings', { userSettings, queryFilter }, 'User settings');
		toast?.(t('PendingSave'), 'success', true);
		return true;
	}

	try {
		const apiUrl = getApiUrl(`Core/UserSettings`);
		const response = await authenticatedFetch(
			apiUrl,
			{
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
				},
				body: JSON.stringify(userSettings),
			},
			'tokenSales'
		);

		if (!response.ok) {
			throw new Error(`HTTP error! status: ${response.status}`);
		}

		await refreshUserSettings();
		log('success', 'UserSetting', `✅ Posted ${userSettings.length} user settings successfully`);
		return true;
	} catch (error) {
		log('error', 'UserSetting', `❌ Failed to post user settings: ${error.message}`);
		return false;
	}
}

export async function refreshUserSettings() {
	userSettingCache.clearMemory();
	await userSettingCache.clear();
}

export async function clearUserSettingCache() {
	await userSettingCache.clearAll();
}

export async function getUserSettingCacheStats() {
	return await userSettingCache.getStats();
}

export async function cleanOldUserSettingCache(maxAgeInDays = 7) {
	await userSettingCache.cleanOld(maxAgeInDays);
}

cleanOldUserSettingCache();
